/**
 * Cumple — Admin: listado de subscriptions (S4-E).
 *
 * Vista compacta para ver el estado del portfolio (cuantos active,
 * trialing, past_due, suspended). No expone acciones — para
 * mark-paid / reactivar pasar por el flujo de payments.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAppShell } from '../components/AppShell.js';

import { ApiError } from '../api/client.js';
import { adminBillingApi, type SubscriptionListItem } from '../api/admin-billing.js';

const STATUS_COLORS: Record<string, string> = {
  trialing: 'bg-blue-100 text-blue-800',
  active: 'bg-emerald-100 text-emerald-800',
  past_due: 'bg-amber-100 text-amber-800',
  suspended: 'bg-red-100 text-red-800',
  cancelled: 'bg-slate-100 text-slate-700',
};

export function AdminSubscriptionsPage(): JSX.Element {
  const { setBackTo } = useAppShell();
  const [subs, setSubs] = useState<SubscriptionListItem[]>([]);

  useEffect(() => {
    setBackTo({ href: '/admin/billing', label: 'Billing' });
    return () => setBackTo(null);
  }, [setBackTo]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminBillingApi
      .listSubscriptions()
      .then((r) => setSubs(r.subscriptions))
      .catch((err: unknown) => {
        if (err instanceof ApiError) setError(`Error ${err.status}: ${err.code}`);
        else setError('Error de red');
      })
      .finally(() => setLoading(false));
  }, []);

  const counts = subs.reduce<Record<string, number>>((acc, s) => {
    acc[s.status] = (acc[s.status] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <main className="mx-auto max-w-6xl px-6 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-brand-900">Subscriptions</h1>
        <p className="text-sm text-slate-500">
          Estado del portfolio. Las mutaciones se hacen via{' '}
          <Link to="/admin/billing" className="text-brand-700 underline">
            payments
          </Link>
          .
        </p>
      </header>

      <div className="mb-4 flex flex-wrap gap-2 text-xs">
        {Object.entries(counts).map(([status, count]) => (
          <span
            key={status}
            className={`rounded px-2 py-1 ${STATUS_COLORS[status] ?? 'bg-slate-100 text-slate-700'}`}
          >
            {status}: <strong>{count}</strong>
          </span>
        ))}
      </div>

      {error && (
        <div role="alert" className="mb-4 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading && <p className="text-sm text-slate-500">Cargando…</p>}

      {!loading && subs.length === 0 && !error && (
        <p className="text-sm text-slate-500">Sin subscriptions.</p>
      )}

      {!loading && subs.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Tenant</th>
                <th className="px-4 py-2">Plan</th>
                <th className="px-4 py-2">Estado</th>
                <th className="px-4 py-2">Ciclo</th>
                <th className="px-4 py-2">Periodo termina</th>
                <th className="px-4 py-2">Provider</th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr key={s.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-medium">{s.tenant_name}</td>
                  <td className="px-4 py-2 capitalize">{s.plan_code}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_COLORS[s.status] ?? 'bg-slate-100 text-slate-700'}`}
                    >
                      {s.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-slate-600">{s.billing_cycle}</td>
                  <td className="px-4 py-2 text-slate-600">
                    {new Date(s.current_period_end).toLocaleDateString('es-DO')}
                  </td>
                  <td className="px-4 py-2 text-slate-600">{s.external_provider ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
